import {
  ButtonDS,
  LaunchPage,
  MaterialUiColumnsProps,
  MaterialUiTable,
  Spacer,
  TextCapitalized,
  StatusSeverity,
  triggerToast,
  usePersistMuiTableState,
} from "@qivia/ui";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import styled from "styled-components";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useGridApiRef } from "@mui/x-data-grid-premium";
import {
  companiesSepaDirectDebitDataAsync,
  companiesSepaDirectDebitSlice,
  getSepaDirectDebitListAsync,
  selectCompaniesSepaDirectDebitData,
  selectCompaniesSepaDirectDebitDataStatus,
  selectResetMemobankMandateBankVerificationProcessStatus,
  selectGetSepaDirectDebitList,
  selectGetSepaDirectDebitListStatus,
  selectUpdateMemobankMandateStatusToActiveStatus,
  selectUpdateMemobankMandateStatusToBankApprovedStatus,
  selectUpdateMemobankMandateStatusToBankRequestedStatus,
} from "../sepaDirectDebitSlice";
import {
  CompanySepaDirectDebitDataList,
  MemobankMandatesDataDisplayed,
  SepaDirectDebitTestStatus,
} from "../sepaDirectDebitApi";
import { selectAdminUser } from "../../homeSlice";
import {
  ExternalIbanModal,
  ExternalIbanModalState,
} from "../../modals/externalIbanModal";
import {
  companyFollowUpSlice,
  createCompanyExternalIbanAsync,
  resetExternalBankAccountAsync,
  selectCreateCompanyExternalIbanStatus,
  selectResetExternalBankAccountStatus,
} from "../../companiesData/followUpSlice";
import { ExternalIbanCell } from "./externalIbanCell";
import { typographies } from "@qivia/ui/src/styles/figmaTypographies";
import { UpdateMandateStatusModal } from "../modals/updateMandateStatusModal";
import { CreateSepaDirectDebitModal } from "../modals/createSepaDirectDebitModal";
import { ResetBankVerificationProcessModal } from "../modals/resetBankVerificationProcessModal";

const renderNextActionCell = (
  row: Record<string, unknown>,
  t: (key: string) => string,
  setSelectedMandateAction: (action: {
    mandateUuid: string | null;
    companyUuid: string | null;
    actionType:
      | "bankRequested"
      | "bankApproved"
      | "active"
      | "testSepaDirectDebit"
      | null;
    testSepaDirectDebitStatus: "SUCCEEDED" | "FAILED" | "PENDING" | null;
  }) => void,
) => {
  const mandateStatus = [
    t("sepaDirectDebit.mandateStatus.COMPLETED"),
    t("sepaDirectDebit.mandateStatus.BANK_REQUESTED"),
    t("sepaDirectDebit.mandateStatus.BANK_APPROVED"),
    t("sepaDirectDebit.mandateStatus.ACTIVE"),
  ].includes(row["mandateStatus"] as string)
    ? row["mandateStatus"]
    : null;

  const sepaDirectDebitTestStatus = row[
    "sepaDirectDebitTestStatus"
  ] as SepaDirectDebitTestStatus | null;
  const mandateUuid = row["mandateUuid"] as string;
  const companyUuid = row["companyUuid"] as string;
  return (
    <StyledNextActionContainer>
      {mandateStatus === t("sepaDirectDebit.mandateStatus.COMPLETED") && (
        <ButtonDS
          text={t("memobankMandates.actions.bankRequested")}
          format={"hug"}
          buttonType={"secondary"}
          sizeButton={"S"}
          onClick={() =>
            setSelectedMandateAction({
              companyUuid,
              mandateUuid,
              actionType: "bankRequested",
              testSepaDirectDebitStatus: sepaDirectDebitTestStatus,
            })
          }
        />
      )}
      {mandateStatus === t("sepaDirectDebit.mandateStatus.BANK_REQUESTED") && (
        <ButtonDS
          text={t("memobankMandates.actions.approveByBank")}
          format={"hug"}
          buttonType={"secondary"}
          sizeButton={"S"}
          onClick={() =>
            setSelectedMandateAction({
              companyUuid,
              mandateUuid,
              actionType: "bankApproved",
              testSepaDirectDebitStatus: sepaDirectDebitTestStatus,
            })
          }
        />
      )}
      {sepaDirectDebitTestStatus === null &&
        mandateStatus === t("sepaDirectDebit.mandateStatus.BANK_APPROVED") && (
          <ButtonDS
            text={t("memobankMandates.actions.testSepaDirectDebit")}
            format={"hug"}
            buttonType={"secondary"}
            sizeButton={"S"}
            onClick={() =>
              setSelectedMandateAction({
                mandateUuid,
                companyUuid,
                actionType: "testSepaDirectDebit",
                testSepaDirectDebitStatus: null,
              })
            }
          />
        )}

      {sepaDirectDebitTestStatus && (
        <TextCapitalized>
          {t(
            `memobankMandates.sepaDirectDebitTestStatus.${sepaDirectDebitTestStatus}`,
          )}
        </TextCapitalized>
      )}
    </StyledNextActionContainer>
  );
};

const renderResetVerificationProcessCell = (
  row: Record<string, unknown>,
  t: (key: string) => string,
  setResetBankVerificationProcessModalState: (state: {
    isVisible: boolean;
    mandateUuid: string | null;
  }) => void,
) => {
  const mandateUuid = row["mandateUuid"] as string | null;
  const mandateStatus = row["mandateStatus"] as string | null;

  if (
    !mandateUuid ||
    mandateStatus === t("sepaDirectDebit.mandateStatus.EXPIRED") ||
    mandateStatus === t("sepaDirectDebit.mandateStatus.SENT")
  ) {
    return null;
  }

  return (
    <StyledNextActionContainer>
      <ButtonDS
        text={t("memobankMandates.actions.resetVerificationProcess")}
        format={"hug"}
        buttonType={"secondary"}
        sizeButton={"S"}
        onClick={() =>
          setResetBankVerificationProcessModalState({
            isVisible: true,
            mandateUuid,
          })
        }
      />
    </StyledNextActionContainer>
  );
};

export const MemobankMandatesTab = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const params = useParams();
  const sepaDirectDebitList: CompanySepaDirectDebitDataList[] = useAppSelector(
    selectCompaniesSepaDirectDebitData,
  );
  const [
    resetBankVerificationProcessModalState,
    setResetBankVerificationProcessModalState,
  ] = useState<{
    isVisible: boolean;
    mandateUuid: string | null;
  }>({
    isVisible: false,
    mandateUuid: null,
  });
  const sepaDirectDebitDataStatus = useAppSelector(
    selectCompaniesSepaDirectDebitDataStatus,
  );
  const updateMemobankMandateStatusToBankRequestedStatus = useAppSelector(
    selectUpdateMemobankMandateStatusToBankRequestedStatus,
  );
  const updateMemobankMandateStatusToBankApprovedStatus = useAppSelector(
    selectUpdateMemobankMandateStatusToBankApprovedStatus,
  );

  const updateMemobankMandateStatusToActiveStatus = useAppSelector(
    selectUpdateMemobankMandateStatusToActiveStatus,
  );
  const resetMemobankMandateBankVerificationProcessStatus = useAppSelector(
    selectResetMemobankMandateBankVerificationProcessStatus,
  );

  const sepaDirectDebitHistoryList = useAppSelector(
    selectGetSepaDirectDebitList,
  );

  const sepaDirectDebitHistoryListStatus = useAppSelector(
    selectGetSepaDirectDebitListStatus,
  );
  const [listDisplayed, setListDisplayed] = useState<
    MemobankMandatesDataDisplayed[] | null
  >(null);
  const adminUser = useAppSelector(selectAdminUser);
  const createCompanyExternalIbanStatus = useAppSelector(
    selectCreateCompanyExternalIbanStatus,
  );

  const resetExternalBankAccountStatus = useAppSelector(
    selectResetExternalBankAccountStatus,
  );

  const [modalState, setModalState] = useState<ExternalIbanModalState>({
    isVisible: false,
  });

  const [resetIbanModalState, setResetIbanModalState] =
    useState<ExternalIbanModalState>({
      isVisible: false,
    });

  const [selectedMandateAction, setSelectedMandateAction] = useState<{
    mandateUuid: string | null;
    companyUuid: string | null;
    actionType:
      | "bankRequested"
      | "bankApproved"
      | "active"
      | "testSepaDirectDebit"
      | null;
    testSepaDirectDebitStatus: SepaDirectDebitTestStatus | null;
  }>({
    mandateUuid: null,
    companyUuid: null,
    actionType: null,
    testSepaDirectDebitStatus: null,
  });

  const handleConfirmExternalIban = useCallback(() => {
    setModalState({
      isVisible: false,
    });
    if (modalState.companyUuid && modalState.iban) {
      void dispatch(
        createCompanyExternalIbanAsync({
          companyUuid: modalState.companyUuid,
          externalIban: modalState.iban,
        }),
      );
    }
  }, [modalState, dispatch]);

  const handleConfirmResetExternalBankAccount = useCallback(() => {
    setResetIbanModalState({
      isVisible: false,
    });
    if (resetIbanModalState.companyUuid && adminUser?.email) {
      void dispatch(
        resetExternalBankAccountAsync({
          companyUuid: resetIbanModalState.companyUuid,
          updatedBy: adminUser.email,
        }),
      );
    }
  }, [resetIbanModalState, adminUser, dispatch]);

  useEffect(() => {
    if (updateMemobankMandateStatusToBankRequestedStatus === "success") {
      triggerToast(t("memobankMandates.actions.success"), "valid");
      setSelectedMandateAction({
        mandateUuid: null,
        companyUuid: null,
        actionType: null,
        testSepaDirectDebitStatus: null,
      });
    } else if (updateMemobankMandateStatusToBankRequestedStatus === "failed") {
      triggerToast(t("memobankMandates.actions.error"), "error");
    }
  }, [updateMemobankMandateStatusToBankRequestedStatus, t]);

  useEffect(() => {
    if (updateMemobankMandateStatusToBankApprovedStatus === "success") {
      triggerToast(t("memobankMandates.actions.success"), "valid");
      setSelectedMandateAction({
        mandateUuid: null,
        companyUuid: null,
        actionType: null,
        testSepaDirectDebitStatus: null,
      });
    } else if (updateMemobankMandateStatusToBankApprovedStatus === "failed") {
      triggerToast(t("memobankMandates.actions.error"), "error");
    }
  }, [updateMemobankMandateStatusToBankApprovedStatus, t]);

  useEffect(() => {
    if (updateMemobankMandateStatusToActiveStatus === "success") {
      triggerToast(t("memobankMandates.actions.success"), "valid");
      setSelectedMandateAction({
        mandateUuid: null,
        companyUuid: null,
        actionType: null,
        testSepaDirectDebitStatus: null,
      });
    } else if (updateMemobankMandateStatusToActiveStatus === "failed") {
      triggerToast(t("memobankMandates.actions.error"), "error");
    }
  }, [updateMemobankMandateStatusToActiveStatus, t]);

  useEffect(() => {
    if (resetMemobankMandateBankVerificationProcessStatus === "success") {
      triggerToast(
        t("memobankMandates.modal.resetVerificationProcess.success"),
        "valid",
      );
      setResetBankVerificationProcessModalState({
        isVisible: false,
        mandateUuid: null,
      });
    } else if (resetMemobankMandateBankVerificationProcessStatus === "failed") {
      triggerToast(
        t("memobankMandates.modal.resetVerificationProcess.error"),
        "error",
      );
    }
  }, [resetMemobankMandateBankVerificationProcessStatus, t]);

  useEffect(() => {
    if (createCompanyExternalIbanStatus === "success") {
      triggerToast(
        t("companyFollowUp.externalIban.create.success") || "",
        "valid",
      );
      dispatch(
        companyFollowUpSlice.actions.resetCreateCompanyExternalIbanStatus(),
      );
      dispatch(
        companiesSepaDirectDebitSlice.actions.resetCompaniesSepaDirectDebitDataStatus(),
      );
    } else if (createCompanyExternalIbanStatus === "failed") {
      triggerToast(
        t("companyFollowUp.externalIban.create.failure") || "",
        "error",
      );
    }
  }, [createCompanyExternalIbanStatus, t, dispatch]);

  useEffect(() => {
    if (resetExternalBankAccountStatus === "success") {
      triggerToast(
        t("companyFollowUp.externalIban.reset.success") || "",
        "valid",
      );
      dispatch(
        companyFollowUpSlice.actions.resetResetExternalBankAccountStatus(),
      );
      dispatch(
        companiesSepaDirectDebitSlice.actions.resetCompaniesSepaDirectDebitDataStatus(),
      );
    } else if (resetExternalBankAccountStatus === "failed") {
      triggerToast(
        t("companyFollowUp.externalIban.reset.failure") || "",
        "error",
      );
    }
  }, [resetExternalBankAccountStatus, t, dispatch]);

  const pageName = "memobankMandates";
  const apiRef = useGridApiRef();
  const key = "customers-grid";

  const aggregation = {
    model: {
      companyName: "size",
      mandateStatus: "size",
      externalIban: "size",
    },
  };

  const dataGridState = usePersistMuiTableState(
    apiRef,
    key,
    pageName,
    aggregation,
  );

  const findKeysToTranslate = useCallback(
    (key: keyof MemobankMandatesDataDisplayed, value: string | number) => {
      switch (key) {
        case "mandateStatus":
          return t(`sepaDirectDebit.mandateStatus.${value}`);
        default:
          return value;
      }
    },
    [t],
  );

  const keysToTranslate = useMemo(
    () => ["mandateStatus"] as (keyof MemobankMandatesDataDisplayed)[],
    [],
  );

  const getLastSepaDirectDebitStatus = useCallback(
    (mandateUuid: string | null) => {
      if (!mandateUuid || !sepaDirectDebitHistoryList) return null;

      const debitsForMandate = sepaDirectDebitHistoryList.filter(
        (debit) => debit.mandate === mandateUuid,
      );
      if (debitsForMandate.length === 0) return null;

      const sortedHistoryList = debitsForMandate.sort((a, b) => {
        return (
          new Date(b.sepaDirectDebitRequestedDate || 0).getTime() -
          new Date(a.sepaDirectDebitRequestedDate || 0).getTime()
        );
      });

      return sortedHistoryList[0].sepaDirectDebitStatus;
    },
    [sepaDirectDebitHistoryList],
  );

  const memobankMandatesListDisplayed: MemobankMandatesDataDisplayed[] =
    useMemo(() => {
      const rowsWithId = sepaDirectDebitList.map((av) => {
        let newDict: CompanySepaDirectDebitDataList & {
          id: string;
          nextAction: string | null;
          resetVerificationProcess: string | null;
          statusSeverity?: StatusSeverity;
        } = {
          ...av,
          id: av.companyUuid,
          nextAction: null,
          resetVerificationProcess: null,
        };

        // Check if the most recent debit for this mandate is FAILED, RETURNED or CANCELED and set the statusSeverity to error for CSS class
        const rawMandateUuid: unknown = av.mandateUuid;
        const mandateUuid: string | null =
          typeof rawMandateUuid === "string" ? rawMandateUuid : null;
        const lastDebitStatus = getLastSepaDirectDebitStatus(mandateUuid);
        if (
          lastDebitStatus &&
          (lastDebitStatus === "FAILED" ||
            lastDebitStatus === "RETURNED" ||
            lastDebitStatus === "CANCELED")
        ) {
          newDict.statusSeverity = "error";
        }

        Object.entries(av).forEach(([key, value]) => {
          if (
            keysToTranslate.includes(key as keyof MemobankMandatesDataDisplayed)
          ) {
            newDict = {
              ...newDict,
              [key]: value
                ? findKeysToTranslate(
                    key as keyof MemobankMandatesDataDisplayed,
                    value as string,
                  )
                : ("" as string),
            };
          }
        });
        return newDict;
      });
      return rowsWithId;
    }, [
      sepaDirectDebitList,
      findKeysToTranslate,
      keysToTranslate,
      getLastSepaDirectDebitStatus,
    ]);

  const searchBarProps = {
    values: memobankMandatesListDisplayed,
    setFilterValues: setListDisplayed,
    keysToIgnore: ["companyUuid" as const],
  };

  useEffect(() => {
    if (sepaDirectDebitDataStatus === "idle") {
      void dispatch(companiesSepaDirectDebitDataAsync());
    }
  }, [sepaDirectDebitDataStatus, dispatch]);

  useEffect(() => {
    if (sepaDirectDebitHistoryListStatus === "idle") {
      void dispatch(getSepaDirectDebitListAsync());
    }
  }, [sepaDirectDebitHistoryListStatus, dispatch, sepaDirectDebitHistoryList]);

  useEffect(() => {
    if (
      memobankMandatesListDisplayed &&
      sepaDirectDebitDataStatus === "success"
    ) {
      setListDisplayed(memobankMandatesListDisplayed);
    }
  }, [sepaDirectDebitDataStatus, memobankMandatesListDisplayed]);

  const columnsDisplayed: {
    [index in keyof MemobankMandatesDataDisplayed]: MaterialUiColumnsProps;
  } = useMemo(() => {
    return {
      companyName: {
        type: "string",
        width: 300,
      },
      externalIban: {
        type: "string",
        width: 360,
        renderCell: (props) => (
          <ExternalIbanCell
            cellProps={props}
            setModalState={setModalState}
            setResetIbanModalState={setResetIbanModalState}
          />
        ),
      },
      mandateStatus: {
        type: "string",
        width: 500,
        height: "30rem",
        valueFormatter: (value: string, row) => {
          const hasMemoBankDirectDebitActive = row[
            "hasMemoBankDirectDebitActive"
          ] as boolean;
          const hasExternalIban = !!row["externalIban"];
          return (
            <>
              {!hasMemoBankDirectDebitActive && hasExternalIban && (
                <StyledWarningContainer>
                  <TextCapitalized>
                    {t(
                      "sepaDirectDebit.hasExternalIban.mandateNotActivated.warning",
                    )}
                  </TextCapitalized>
                </StyledWarningContainer>
              )}
              <TextCapitalized>{value}</TextCapitalized>
            </>
          );
        },
      },
      nextAction: {
        type: "string",
        valueFormatter: (value: string, row: Record<string, unknown>) =>
          renderNextActionCell(row, t, setSelectedMandateAction),
      },
      resetVerificationProcess: {
        type: "string",
        valueFormatter: (value: string, row: Record<string, unknown>) =>
          renderResetVerificationProcessCell(
            row,
            t,
            setResetBankVerificationProcessModalState,
          ),
      },
    };
  }, [t]);

  if (!listDisplayed) return <LaunchPage hasBorderRadius={true} />;

  if (
    params.tab !== "memobankMandates" ||
    adminUser?.email === "exterieur1@qivia.fr"
  ) {
    return;
  }
  return (
    <StyledContainer>
      <StyledExplanationContainer>
        <TextCapitalized>
          {t(
            "sepaDirectDebit.hasExternalIban.mandateNotActivated.explanation.part1",
          )}
        </TextCapitalized>
        <Spacer y={0.5} />
        <TextCapitalized>
          {t(
            "sepaDirectDebit.hasExternalIban.mandateNotActivated.explanation.part2",
          )}
        </TextCapitalized>
        <Spacer y={2} />
        <TextCapitalized>
          {t(
            "sepaDirectDebit.hasExternalIban.mandateNotActivated.explanation.part3",
          )}
        </TextCapitalized>
        <Spacer y={1} />
      </StyledExplanationContainer>
      <ExternalIbanModal
        visible={modalState.isVisible}
        onClose={() => setModalState({ isVisible: false })}
        selectedIban={modalState.iban}
        selectedName={modalState.name}
        onConfirm={handleConfirmExternalIban}
        modalType="ADD"
      />
      <ExternalIbanModal
        visible={resetIbanModalState.isVisible}
        onClose={() => setResetIbanModalState({ isVisible: false })}
        selectedIban={resetIbanModalState.iban}
        selectedName={resetIbanModalState.name}
        onConfirm={handleConfirmResetExternalBankAccount}
        modalType="RESET"
      />
      <UpdateMandateStatusModal
        actionType={
          selectedMandateAction.actionType as
            | "bankRequested"
            | "bankApproved"
            | "active"
            | null
        }
        isVisible={
          selectedMandateAction.actionType !== null &&
          selectedMandateAction.actionType !== "testSepaDirectDebit" &&
          selectedMandateAction.companyUuid !== null &&
          selectedMandateAction.mandateUuid !== null
        }
        selectedMandateUuid={selectedMandateAction.mandateUuid}
        onClose={() =>
          setSelectedMandateAction({
            mandateUuid: null,
            companyUuid: null,
            actionType: null,
            testSepaDirectDebitStatus: null,
          })
        }
      />
      <CreateSepaDirectDebitModal
        visible={selectedMandateAction.actionType === "testSepaDirectDebit"}
        onCloseModal={() =>
          setSelectedMandateAction({
            mandateUuid: null,
            companyUuid: null,
            actionType: null,
            testSepaDirectDebitStatus: null,
          })
        }
        currentSepaDirectDebitCompanyUuid={
          selectedMandateAction.companyUuid as string
        }
      />
      <ResetBankVerificationProcessModal
        visible={resetBankVerificationProcessModalState.isVisible}
        onCloseModal={() =>
          setResetBankVerificationProcessModalState({
            isVisible: false,
            mandateUuid: null,
          })
        }
        currentMandateCompanyUuid={
          resetBankVerificationProcessModalState.mandateUuid as string
        }
      />
      <Spacer y={1} />
      <MaterialUiTable<
        keyof MemobankMandatesDataDisplayed,
        MemobankMandatesDataDisplayed
      >
        rows={listDisplayed}
        columns={columnsDisplayed}
        pageName={pageName}
        dataGridState={dataGridState}
        searchBar={searchBarProps}
        apiRef={apiRef}
      />
    </StyledContainer>
  );
};

const StyledWarningContainer = styled.div`
  overflow-x: auto;
`;

const StyledExplanationContainer = styled.div`
  ${typographies["Body/XS"]};
`;

const StyledNextActionContainer = styled.div`
  display: flex;
  height: 100%;
  align-items: center;
`;

const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;
